/**
 * Formatação em pt-BR e leitura defensiva de contagens.
 *
 * Vive fora dos componentes porque data mal formatada é erro de leitura, não
 * de estilo: quem opera decide olhando "18/06 14:30" no fuso do próprio
 * navegador, não um ISO em UTC. E porque o mesmo formato tem de valer em
 * todas as cinco telas — divergência aqui vira desconfiança no número.
 */

const FMT_DATA_HORA = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

const FMT_DATA = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
});

const FMT_NUM = new Intl.NumberFormat("pt-BR");

function instante(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? null : t;
}

export function dataHora(iso: string | null | undefined, vazio = "—"): string {
  const t = instante(iso);
  return t ? FMT_DATA_HORA.format(t) : vazio;
}

export function data(iso: string | null | undefined, vazio = "—"): string {
  const t = instante(iso);
  return t ? FMT_DATA.format(t) : vazio;
}

/** "há 3 h" lê melhor que um carimbo quando a pergunta é "ainda está vivo?". */
export function relativo(iso: string | null | undefined, vazio = "nunca"): string {
  const t = instante(iso);
  if (!t) return vazio;
  const seg = Math.round((Date.now() - t.getTime()) / 1000);
  const futuro = seg < 0;
  const s = Math.abs(seg);
  const texto =
    s < 60 ? "menos de 1 min"
    : s < 3600 ? `${Math.floor(s / 60)} min`
    : s < 86400 ? `${Math.floor(s / 3600)} h`
    : `${Math.floor(s / 86400)} d`;
  if (s < 60 && !futuro) return "agora";
  return futuro ? `em ${texto}` : `há ${texto}`;
}

export function num(n: number | null | undefined, vazio = "—"): string {
  return typeof n === "number" && Number.isFinite(n) ? FMT_NUM.format(n) : vazio;
}

/** Uma casa decimal — mais que isso é falsa precisão em métrica de operação. */
export function dec(n: number | null | undefined, vazio = "—"): string {
  if (typeof n !== "number" || !Number.isFinite(n)) return vazio;
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}

/**
 * Contagem por chave, tolerante à ausência.
 * O contrato promete um registro completo; o servidor pode omitir a chave que
 * está zerada. Ler como `undefined` e mostrar 0 é mais honesto do que quebrar.
 */
export function conta(
  registro: Record<string, number | undefined> | null | undefined,
  chave: string,
): number {
  return registro?.[chave] ?? 0;
}

/** Plural sem gambiarra de template espalhada pelas telas. */
export function plural(n: number, um: string, muitos: string): string {
  return `${FMT_NUM.format(n)} ${n === 1 ? um : muitos}`;
}
